import { ApplicationConfig } from "@/config/appConfig";
import { LocalStorageKeys } from "@/const/keys";
import {
  generateColorFromId,
  COLORFUL_BACKGROUNDS,
  GroupInfo,
  UserInfo
} from "@/services/login-service/entities/UserLoginEntity";
import { SettingService } from "@/services/setting-service/SettingService";
import { create } from "zustand";

export type UserWithSelection = UserInfo & { checked: boolean; isCurrentUser?: boolean };
export type GroupWithSelection = Pick<GroupInfo, "id" | "groupName" | "isCustomGroup"> & {
  checked: boolean;
  members: UserWithSelection[];
};

type State = {
  groups: GroupWithSelection[];
  selectedUsersInPage: UserWithSelection[];
  pageIndex: number;
  pageSize: number;
};

type Action = {
  selectUserById: (userId: string, groupId: string, checked: boolean) => void;
  selectGroupById: (groupId: string, checked: boolean) => void;
  initializeSelectedUsers: (
    groups: GroupInfo[],
    defaultGroupId?: string,
    groupOrder?: string
  ) => void;
  isShowNextPageButton: () => boolean;
  isShowPreviousPageButton: () => boolean;
  replaceSelectedUsersFromGroupId: (users: UserWithSelection[]) => void;
  replaceGroups: (groups: GroupWithSelection[]) => void;
  saveGroupOrdersToServer: () => Promise<void>;
  getAllGroupUsers: () => UserWithSelection[];

  getDefaultSelectedGroupsFromSessionStorage: () => GroupWithSelection[];
  saveDefaultSelectedGroupsToSessionStorage: () => void;
  clearDefaultSelectedGroupsFromSessionStorage: () => void;

  setPageSize: (pageSize: number) => void;
  setPageIndex: (pageIndex: number) => void;
  resetPageSize: () => void;
};

const DEFAULT_PAGE_SIZE = localStorage.getItem(LocalStorageKeys.PAGE_SIZE)
  ? Number(localStorage.getItem(LocalStorageKeys.PAGE_SIZE))
  : ApplicationConfig.defaultShowUserPaging;

const userStore = create<State & Action>()((set, get) => ({
  groups: [],
  selectedUsersInPage: [],
  pageIndex: 0,
  pageSize: DEFAULT_PAGE_SIZE,

  setPageIndex: (pageIndex: number) => {
    if (pageIndex < 0) {
      return;
    }

    const { groups, pageSize } = get();
    const groupSelected = groups.filter((g) => g.checked);
    const newSelectedUsersInPage = groupSelected
      .flatMap((group) => group.members)
      .filter((user) => user.checked)
      .slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    set({
      pageIndex,
      selectedUsersInPage: newSelectedUsersInPage
    });
  },

  resetPageSize: () => {
    set({
      pageSize: ApplicationConfig.defaultShowUserPaging,
      pageIndex: 0
    });

    localStorage.removeItem(LocalStorageKeys.PAGE_SIZE);
  },

  setPageSize: (pageSize: number) => {
    const { groups } = get();
    const newPageIndex = 0;
    const groupSelected = groups.filter((g) => g.checked);
    const newSelectedUsersInPage = groupSelected
      .flatMap((group) => group.members)
      .filter((user) => user.checked)
      .slice(newPageIndex * pageSize, (newPageIndex + 1) * pageSize);

    set({
      pageSize,
      pageIndex: newPageIndex,
      selectedUsersInPage: newSelectedUsersInPage
    });

    localStorage.setItem(LocalStorageKeys.PAGE_SIZE, String(pageSize));
  },

  getDefaultSelectedGroupsFromSessionStorage: () => {
    const defaultSelectedGroups = sessionStorage.getItem(LocalStorageKeys.DEFAULT_SELECTED_GROUPS);
    if (!defaultSelectedGroups) {
      return [];
    }

    return JSON.parse(defaultSelectedGroups) as GroupWithSelection[];
  },

  saveDefaultSelectedGroupsToSessionStorage: () => {
    const { groups } = get();
    sessionStorage.setItem(LocalStorageKeys.DEFAULT_SELECTED_GROUPS, JSON.stringify(groups));
  },

  clearDefaultSelectedGroupsFromSessionStorage: () => {
    sessionStorage.removeItem(LocalStorageKeys.DEFAULT_SELECTED_GROUPS);
  },

  initializeSelectedUsers: (groups: GroupInfo[], defaultGroupId?: string, groupOrder?: string) => {
    const groupOrders = groupOrder ? JSON.parse(groupOrder) : null;
    const { pageSize } = get();

    const selectedDefaultGroupId = defaultGroupId && defaultGroupId.trim() ? defaultGroupId : "";

    const groupOrdersMap = new Map<
      string,
      { id: string; checked: boolean; members: Array<{ id: string; checked: boolean }> }
    >();
    const groupOrderIds: string[] = [];

    if (groupOrders && Array.isArray(groupOrders)) {
      groupOrders.forEach(
        (groupOrder: {
          id: string;
          checked: boolean;
          members: Array<{ id: string; checked: boolean }>;
        }) => {
          groupOrdersMap.set(groupOrder.id, groupOrder);
          groupOrderIds.push(groupOrder.id);
        }
      );
    }

    const defaultSelectedGroups = get().getDefaultSelectedGroupsFromSessionStorage();

    // Collect all members that need colors and assign unique colors
    const membersNeedingColors: Array<{ id: string; groupId: string }> = [];
    const existingColors = new Set<string>();

    groups.forEach((group) => {
      if (!groupOrders) {
        group.members = group.members.sort((a, b) => a.name.localeCompare(b.name));
      }

      group.members.forEach((member) => {
        if (!member.color) {
          membersNeedingColors.push({ id: member.id, groupId: group.id });
        } else {
          existingColors.add(member.color);
        }
      });
    });

    const colorAssignmentMap = new Map<string, string>();
    let colorIndex = 0;

    membersNeedingColors.forEach((member) => {
      while (colorIndex < COLORFUL_BACKGROUNDS.length) {
        const color = COLORFUL_BACKGROUNDS[colorIndex];
        if (!existingColors.has(color)) {
          colorAssignmentMap.set(member.id, color);
          existingColors.add(color);
          colorIndex++;
          break;
        }
        colorIndex++;
      }
      if (!colorAssignmentMap.has(member.id)) {
        colorAssignmentMap.set(member.id, generateColorFromId(member.id));
      }
    });

    const groupsWithSelection: GroupWithSelection[] = groups.map((group) => {
      const savedGroupOrder = groupOrdersMap.get(group.id);

      const savedGroupInSessionStorage = defaultSelectedGroups.find((g) => g.id === group.id);
      let isChecked = group.id === selectedDefaultGroupId;

      if (savedGroupInSessionStorage) {
        isChecked = savedGroupInSessionStorage?.checked;
      }

      const memberOrdersMap = new Map<string, { id: string; checked: boolean }>();
      const memberOrderIds: string[] = [];

      if (savedGroupOrder?.members && Array.isArray(savedGroupOrder.members)) {
        savedGroupOrder.members.forEach((memberOrder: { id: string; checked: boolean }) => {
          memberOrdersMap.set(memberOrder.id, memberOrder);
          memberOrderIds.push(memberOrder.id);
        });
      }

      const membersInOrder: UserWithSelection[] = [];
      const membersNotInOrder: UserWithSelection[] = [];

      group.members.forEach((member) => {
        let memberChecked = isChecked;
        const savedMemberOrder = savedGroupInSessionStorage?.members.find(
          (m) => m.id === member.id
        );

        if (savedMemberOrder) {
          memberChecked = savedMemberOrder?.checked;
        }

        const memberWithSelection: UserWithSelection = {
          ...member,
          checked: memberChecked,
          groupId: group.id,
          color: member.color || colorAssignmentMap.get(member.id) || generateColorFromId(member.id)
        };

        if (memberOrdersMap.has(member.id)) {
          membersInOrder.push(memberWithSelection);
          return;
        }

        membersNotInOrder.push(memberWithSelection);
      });

      membersInOrder.sort((a, b) => {
        const indexA = memberOrderIds.indexOf(a.id);
        const indexB = memberOrderIds.indexOf(b.id);
        return indexA - indexB;
      });

      const sortedMembers = [...membersInOrder, ...membersNotInOrder];

      const newGroup: GroupWithSelection = {
        ...group,
        id: group.id,
        groupName: group.groupName,
        isCustomGroup: group.isCustomGroup || false,
        checked: isChecked,
        members: sortedMembers
      };

      return newGroup;
    });

    const groupsInOrder: GroupWithSelection[] = [];
    const groupsNotInOrder: GroupWithSelection[] = [];

    groupsWithSelection.forEach((group) => {
      if (groupOrdersMap.has(group.id)) {
        groupsInOrder.push(group);
        return;
      }

      groupsNotInOrder.push(group);
    });

    groupsInOrder.sort((a, b) => {
      const indexA = groupOrderIds.indexOf(a.id);
      const indexB = groupOrderIds.indexOf(b.id);
      return indexA - indexB;
    });

    const sortedGroups = [...groupsInOrder, ...groupsNotInOrder];

    const groupSelected = sortedGroups.filter((group) => group.checked);
    const selectedDataUsersInPage = groupSelected
      .flatMap((group) => group.members)
      .filter((user) => user.checked)
      .slice(0, pageSize);

    set({
      selectedUsersInPage: selectedDataUsersInPage,
      pageIndex: 0,
      groups: sortedGroups
    });
  },

  getAllGroupUsers: () =>
    get()
      .groups.map((x) => x.members)
      .flat() ?? [],

  saveGroupOrdersToServer: async () => {
    const { groups } = get();
    const settingService = new SettingService();
    const groupOrdersJson = JSON.stringify(
      groups.map((group) => ({
        id: group.id,
        checked: group.checked,
        members: group.members.map((member) => {
          return {
            id: member.id,
            checked: member.checked
          };
        })
      }))
    );

    await settingService.setGroupOrder(groupOrdersJson);
  },

  selectUserById: (userId: string, groupId: string, checked: boolean) => {
    const { groups, pageSize, pageIndex, saveDefaultSelectedGroupsToSessionStorage } = get();
    const groupById = groups.find((g) => g.id === groupId);

    if (!groupById) return;

    groupById.members = groupById.members.map((x) => {
      if (x.id === userId) {
        x.checked = checked;
      }

      return x;
    });

    if (!groupById.checked && checked) {
      groupById.checked = true;
    }

    const newSelectedUsersInPage = groups
      .filter((g) => g.checked)
      .flatMap((group) => group.members)
      .filter((user) => user.checked)
      .slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    set({
      groups: groups,
      selectedUsersInPage: newSelectedUsersInPage
    });

    saveDefaultSelectedGroupsToSessionStorage();
  },

  selectGroupById: (groupId: string, checked: boolean) => {
    const { pageSize, groups, saveDefaultSelectedGroupsToSessionStorage } = get();

    groups.forEach((group) => {
      if (group.id === groupId) {
        group.checked = checked;
        if (group.members.length > 0) {
          group.members.forEach((member) => {
            member.checked = checked;
          });
        }
        return;
      }
    });

    const newPageIndex = 0;
    const groupSelected = groups.filter((group) => group.checked);
    const newSelectedUsersInPage = groupSelected
      .flatMap((group) => group.members)
      .filter((user) => user.checked)
      .slice(newPageIndex * pageSize, (newPageIndex + 1) * pageSize);

    set({
      selectedUsersInPage: newSelectedUsersInPage,
      pageIndex: newPageIndex,
      groups: groups
    });

    saveDefaultSelectedGroupsToSessionStorage();
  },

  replaceSelectedUsersFromGroupId: (users: UserWithSelection[]) => {
    const { groups, pageIndex, pageSize, saveGroupOrdersToServer } = get();
    const groupUsersUpdated = new Set<string>(users.map((x) => x.groupId));

    groupUsersUpdated.forEach((groupId) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) {
        return;
      }

      group.members = users;
    });

    const newSelectedUsersInPage = groups
      .filter((g) => g.checked)
      .flatMap((group) => group.members)
      .filter((user) => user.checked)
      .slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    set({
      groups: groups,
      selectedUsersInPage: newSelectedUsersInPage
    });

    saveGroupOrdersToServer();
  },

  replaceGroups: (newGroups: GroupWithSelection[]) => {
    const { groups, pageIndex, pageSize, saveGroupOrdersToServer } = get();

    // Reorder users to match new group order
    const newGroupOrders: GroupWithSelection[] = [];
    newGroups.forEach((group) => {
      const groupUsers = groups.find((g) => g.id === group.id);
      if (!groupUsers) {
        return;
      }

      newGroupOrders.push(groupUsers);
    });

    const newSelectedUsersInPage = newGroups
      .filter((g) => g.checked)
      .flatMap((group) => group.members)
      .filter((user) => user.checked)
      .slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    set({
      groups: newGroups,
      selectedUsersInPage: newSelectedUsersInPage
    });

    saveGroupOrdersToServer();
  },

  isShowNextPageButton: () => {
    const { pageIndex, pageSize, groups } = get();
    const groupSelected = groups.filter((g) => g.checked);
    if (!groupSelected) return false;

    const selectedUsersCount = groupSelected
      .flatMap((group) => group.members)
      .filter((user) => user.checked).length;
    const maxPage = Math.ceil(selectedUsersCount / pageSize) - 1;
    return pageIndex < maxPage;
  },

  isShowPreviousPageButton: () => {
    const { pageIndex } = get();
    return pageIndex > 0;
  }
}));

export default userStore;
